/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ModelRepresentation, ResultListDataRepresentationModelRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class ModelsApi extends BaseApi {
    createModel(modelRepresentation) {
        throwIfNotDefined(modelRepresentation, 'modelRepresentation');
        return this.post({
            path: '/api/enterprise/models',
            bodyParam: modelRepresentation,
            returnType: ModelRepresentation
        });
    }
    deleteModel(modelId, opts) {
        throwIfNotDefined(modelId, 'modelId');
        const pathParams = {
            modelId
        };
        const queryParams = {
            cascade: opts === null || opts === void 0 ? void 0 : opts.cascade,
            deleteRuntimeApp: opts === null || opts === void 0 ? void 0 : opts.deleteRuntimeApp
        };
        return this.delete({
            path: '/api/enterprise/models/{modelId}',
            pathParams,
            queryParams
        });
    }
    duplicateModel(modelId, modelRepresentation) {
        throwIfNotDefined(modelId, 'modelId');
        throwIfNotDefined(modelRepresentation, 'modelRepresentation');
        const pathParams = {
            modelId
        };
        return this.post({
            path: '/api/enterprise/models/{modelId}/clone',
            pathParams,
            bodyParam: modelRepresentation,
            returnType: ModelRepresentation
        });
    }
    getModelJSON(modelId) {
        throwIfNotDefined(modelId, 'modelId');
        const pathParams = {
            modelId
        };
        return this.get({
            path: '/api/enterprise/models/{modelId}/editor/json',
            pathParams
        });
    }
    getModelThumbnail(modelId) {
        throwIfNotDefined(modelId, 'modelId');
        const pathParams = {
            modelId
        };
        return this.get({
            path: '/api/enterprise/models/{modelId}/thumbnail',
            pathParams,
            accepts: ['image/png']
        });
    }
    getModel(modelId, opts) {
        throwIfNotDefined(modelId, 'modelId');
        const pathParams = {
            modelId
        };
        const queryParams = {
            includePermissions: opts === null || opts === void 0 ? void 0 : opts.includePermissions
        };
        return this.get({
            path: '/api/enterprise/models/{modelId}',
            pathParams,
            queryParams,
            returnType: ModelRepresentation
        });
    }
    getModelsToIncludeInAppDefinition() {
        return this.get({
            path: '/api/enterprise/models-for-app-definition',
            returnType: ResultListDataRepresentationModelRepresentation
        });
    }
    getModels(opts) {
        return this.get({
            path: '/api/enterprise/models',
            queryParams: opts,
            returnType: ResultListDataRepresentationModelRepresentation
        });
    }
    importNewVersion(modelId, file) {
        throwIfNotDefined(modelId, 'modelId');
        throwIfNotDefined(file, 'file');
        const pathParams = {
            modelId
        };
        const formParams = {
            file
        };
        return this.post({
            path: '/api/enterprise/models/{modelId}/newversion',
            pathParams,
            formParams,
            contentTypes: ['multipart/form-data'],
            returnType: ModelRepresentation
        });
    }
    importProcessModel(file) {
        throwIfNotDefined(file, 'file');
        const formParams = {
            file
        };
        return this.post({
            path: '/api/enterprise/process-models/import',
            contentTypes: ['multipart/form-data'],
            formParams,
            returnType: ModelRepresentation
        });
    }
    saveModel(modelId, values) {
        throwIfNotDefined(modelId, 'modelId');
        throwIfNotDefined(values, 'values');
        const pathParams = {
            modelId
        };
        return this.post({
            path: '/api/enterprise/models/{modelId}/editor/json',
            pathParams,
            bodyParam: values,
            returnType: ModelRepresentation
        });
    }
    updateModel(modelId, updatedModel) {
        throwIfNotDefined(modelId, 'modelId');
        throwIfNotDefined(updatedModel, 'updatedModel');
        const pathParams = {
            modelId
        };
        return this.put({
            path: '/api/enterprise/models/{modelId}',
            pathParams,
            bodyParam: updatedModel,
            returnType: ModelRepresentation
        });
    }
    validateModel(modelId, opts) {
        throwIfNotDefined(modelId, 'modelId');
        const postBody = opts === null || opts === void 0 ? void 0 : opts.values;
        const pathParams = {
            modelId
        };
        return this.post({
            path: '/api/enterprise/models/{modelId}/editor/validate',
            pathParams,
            bodyParam: postBody,
            contentTypes: ['application/x-www-form-urlencoded']
        });
    }
}
